import { useEffect, useState, type ReactNode } from 'react';
import { Stack, Text, TextInput } from '@mantine/core';
import { Button } from './Button';
import { Modal } from './Modal';

export interface ConfirmDialogProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /** Body copy — say exactly what will be destroyed and whether it is reversible. */
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  /** Visual weight of the confirm button. */
  tone?: 'danger' | 'primary';
  /**
   * When set, the operator must TYPE this string before the confirm button
   * enables. Use the resource id for irreversible teardown; omit for merely
   * disruptive actions where an extra click is enough.
   */
  confirmText?: string;
}

/**
 * ConfirmDialog — guard for destructive actions.
 *
 * Destroy used to be a plain Yes/No on an irreversible teardown of every VM in
 * a range, and Stop-lab had no confirmation at all. `confirmText` adds the
 * type-the-id speed bump for the irreversible cases.
 */
export function ConfirmDialog({
  opened,
  onClose,
  onConfirm,
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  loading = false,
  tone = 'danger',
  confirmText,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');

  // Reset the guard every time the dialog opens so a previous confirmation
  // can't pre-arm the next one.
  useEffect(() => {
    if (opened) setTyped('');
  }, [opened]);

  const armed = !confirmText || typed.trim() === confirmText;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={tone} loading={loading} disabled={!armed} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <Stack gap="sm">
        {typeof children === 'string' ? (
          <Text size="sm" c="dimmed">
            {children}
          </Text>
        ) : (
          children
        )}
        {confirmText ? (
          <TextInput
            label={
              <Text size="sm">
                Type <span className="font-mono">{confirmText}</span> to confirm
              </Text>
            }
            value={typed}
            onChange={(e) => setTyped(e.currentTarget.value)}
            placeholder={confirmText}
            autoComplete="off"
            spellCheck={false}
            size="sm"
          />
        ) : null}
      </Stack>
    </Modal>
  );
}
